import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { cn } from '@/lib/cn';

export interface TabItem<T = string> {
  name: T;
  container?: ReactNode;
  label?: string;
  disabled?: boolean;
  disabledReason?: string;
  isError?: boolean;
}

export interface TabButtonsProps<T = string> {
  disabledTooltipId?: string;
  tabs: TabItem<T>[];
  activeTab?: string;
  tabNameParam?: string;
  setActiveTab?: Dispatch<SetStateAction<T>> | ((tab: T) => void);
  className?: string;
}

export const TabButtons = <T extends string>({
  tabs,
  activeTab,
  setActiveTab,
  className,
}: TabButtonsProps<T>) => {
  return (
    <div className={cn('w-full max-w-full min-w-0 select-none', className)}>
      <div className="flex w-max min-w-max items-center gap-4">
        {tabs.map(({ name, label, disabled, disabledReason, isError }, i) => (
          <button
            key={`tab_${i}_${String(name)}`}
            disabled={disabled}
            className={cn(
              'relative flex shrink-0 items-center text-sm leading-[22px] font-medium',
              {
                'text-gray-500': activeTab !== name,
                'rounded-underline': activeTab === name,
                'bg-red-700 bg-opacity-80 !text-black px-2 rounded-lg':
                  isError && activeTab !== name,
              },
            )}
            title={disabled ? disabledReason : undefined}
            onClick={() => setActiveTab?.(name)}
          >
            <span>{label || (name as string)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
